import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMediaQuery } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Divider from '@material-ui/core/Divider';
import RoomIcon from '@material-ui/icons/Room';
import Collapse from '@material-ui/core/Collapse';
import CheckIcon from '@material-ui/icons/Check';
import moment from 'moment';
import CustomerCard from '../components/CustomerCard';
import { getZ3PConfigValue, roundToTwoDecimals } from '../helper';
// import AppointmentStatus from './AppointmentStatus';
import ETABox from './ETABox';

const useStyles = makeStyles((theme) => ({
  serviceCard: {
    display: 'flex',
    width: '100%',
    background: '#ffffff',
  },
  fullWidth: {
    width: '100%',
  },
  serviceFooter: {
    position: 'relative',
  },
  serviceFooterTxt: {
    fontSize: 12,
    position: 'absolute',
    top: -6,
    right: 80,
    backgroundColor: '#fff',
    padding: '0 5px',
    height: '20px !important',
    margin: '0px 5px',
  },
  disableContainer: {
    opacity: '0.3',
  },
  footerDivider: {
    border: '.5px solid whitesmoke',
    margin: '15px 0',
  },
  listInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      padding: '5px 0px',
    },
  },
  listDivider: {
    margin: '10px 0',
  },
  listlabel: {
    color: '#c1c1c1',
  },
  listIndicator: {
    background: '#cecece',
    width: 4,
    height: 4,
    borderRadius: '50%',
    marginRight: 8,
    marginTop: 7,
    marginLeft: 8,
    display: 'table',
  },
  displayList: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    marginTop: 8,
    fontSize: 12,
    color: '#959393',
  },
  countCircle: {
    width: 10,
    height: 10,
    padding: 3,
    borderRadius: '50%',
    color: '#fff',
    fontSize: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 'inherit',
  },
  countDivider: {
    backgroundColor: 'rgba(0, 0, 0, 0.26)',
    width: 1,
    height: '100%',
    marginBottom: 5,
    marginTop: 10,
  },
}));

const DragContainer = ({ stop, drag, isEditing, i, lastItem }) => {
  const {
    address = '',
    scheduled_works = [],
    estimated_arrival_time,
    estimated_departure_time,
  } = stop || {};
  const { status = null, customer } = scheduled_works[0] || {};

  let min_planned_arrival_time = moment('23:59:59', 'HH:mm:ss');
  let max_planned_departure_time = moment('00:00:00', 'HH:mm:ss');

  scheduled_works.forEach((sw) => {
    const plannedArrivalTime = moment(sw.planned_arrival_time, 'HH:mm:ss');
    const plannedDepartureTime = moment(sw.planned_departure_time, 'HH:mm:ss');
    if (plannedArrivalTime < min_planned_arrival_time) {
      min_planned_arrival_time = plannedArrivalTime;
    }
    if (plannedDepartureTime > max_planned_departure_time) {
      max_planned_departure_time = plannedDepartureTime;
    }
  });

  const estimatedArrivalTime = estimated_arrival_time
    ? moment.utc(estimated_arrival_time).local(true)
    : null;
  const estimatedDepartureTime = estimated_departure_time
    ? moment.utc(estimated_departure_time).local(true)
    : null;

  const classes = useStyles();
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const allSwsAreFinished = scheduled_works.every(
    (sw) => sw.status === 'finished',
  );

  const [isExpanded, setIsExpanded] = useState(!allSwsAreFinished);

  const handleServiceCardClick = () => {
    setIsExpanded(!isExpanded);
  };

  const showEtas = !!getZ3PConfigValue('is_route_eta_feature_enabled');

  return (
    <Box
      className={classes.serviceCard}
      {...drag}
      onClick={handleServiceCardClick}
    >
      <Box className={`${classes.fullWidth}`}>
        {!status && (
          <Box display="flex" ml={0.5} minHeight={50}>
            <Box
              display="flex"
              alignItems="center"
              flexDirection="column"
              mr={0.6}
              mt={0.5}
              color="rgba(0, 0, 0, 0.26)"
            >
              <RoomIcon />
              {!lastItem && <span className={classes.countDivider} />}
            </Box>
            <Box mr={2} mt={1} mb={1} display="flex">
              <Box variant="body2" fontSize={12} className={classes.listlabel}>
                {address}
              </Box>
            </Box>
          </Box>
        )}
        {scheduled_works.length > 0 && (
          <Box display="flex" ml={1}>
            <Box
              display="flex"
              alignItems="center"
              flexDirection="column"
              mr={0.6}
              mt={0.5}
            >
              <span
                style={{ backgroundColor: 'rgba(0, 0, 0, 0.26)' }}
                className={classes.countCircle}
              >
                {i + 1}
              </span>
              <span className={classes.countDivider} />
            </Box>
            <Box
              mt={1}
              mb={3}
              display="flex"
              mx={1}
              width="100%"
              style={{
                backgroundColor: allSwsAreFinished ? '#f3f3f3' : 'white',
                borderRadius: '5px',
                boxShadow: '0px 1px 3px #a5a3a3',
                padding: 10,
              }}
            >
              <Box width={isSmall ? 'max-content' : '100%'}>
                <Box
                  display="flex"
                  alignItems="flex-start"
                  flexDirection={isSmall ? 'column' : 'row'}
                >
                  <Box width="100%" pr={1}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                    {customer && <CustomerCard record={scheduled_works[0]} storderId={scheduled_works.st_order_id} />}

                      <ETABox
                        requestedArrival={min_planned_arrival_time}
                        requestedDeparture={max_planned_departure_time}
                        estimatedArrival={estimatedArrivalTime}
                        estimatedDeparture={estimatedDepartureTime}
                        showEta={!isEditing && showEtas && !allSwsAreFinished}
                      />

                      {allSwsAreFinished && (
                        <CheckIcon
                          color="action"
                          style={{ display: 'inline', color: 'green' }}
                        />
                      )}
                    </Box>
                    <Box className={classes.displayList}>{address}</Box>
                    {!isEditing &&
                      scheduled_works.map((sw) => {
                        const { id, vehicle, services = [] } = sw;
                        const {
                          display_name,
                          license_plate_number,
                          license_plate_state,
                        } = vehicle || {};

                        let serviceHours = 0;
                        const serviceNames = services.reduce((acc, next) => {
                          serviceHours += next.labor_hours;
                          return `${acc}${acc.length ? ', ' : ''} ${
                            next.display_name
                          }`;
                        }, '');

                        return (
                          <>
                            <Collapse in={isExpanded}>
                              <Divider className={classes.listDivider} />
                              {display_name && (
                                <Box className={classes.displayList}>
                                  <span className={classes.listIndicator} />
                                  {display_name}
                                </Box>
                              )}
                              {license_plate_state && (
                                <Box className={classes.displayList}>
                                  <span className={classes.listIndicator} />
                                  {`${license_plate_number} ${license_plate_state}`}
                                </Box>
                              )}
                              <Box className={classes.displayList}>
                                <span className={classes.listIndicator} />
                                {`${roundToTwoDecimals(
                                  serviceHours,
                                )} hrs - ${serviceNames}`}
                              </Box>
                              <Box mt={1} fontSize={12}>
                                <Link
                                  to={`/scheduled-work/${id}/show`}
                                >
                                  Manage Appointment
                                </Link>
                              </Box>
                            </Collapse>
                            {/* <Divider className={classes.listDivider} />
                            <Box mt={1}>
                              <AppointmentStatus />
                            </Box> */}
                          </>
                        );
                      })}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DragContainer;

export const ContainerFooter = ({ minutes, miles }) => {
  const classes = useStyles();
  return (
    <>
      {minutes !== 0 && miles !== 0 && (
        <Box className={classes.serviceFooter}>
          <hr className={classes.footerDivider} />
          <Box className={classes.serviceFooterTxt}>
            {`${minutes} mins - ${miles} miles`}
          </Box>
        </Box>
      )}
    </>
  );
};
